import React, { useState } from 'react';
import { Box, Typography, IconButton, CircularProgress, Alert, Snackbar, Tooltip } from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import MeterGrid from './MeterGrid';
import { useCapabilities } from '../contexts/CapabilitiesContext';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';

// Standalone page: the same "Meter Health Monitor" grid the dashboard shows, plus a
// one-click export of every meter/product's data and control charts to Excel.
const MeterFactorControlCharts: React.FC<{ userEmail?: string }> = () => {
  const { getIdTokenClaims } = useAuth0();
  // The export endpoint now enforces export_data. Hide the control rather than leave a button that
  // 403s -- a visible action that always fails is worse than one that isn't offered.
  const { can } = useCapabilities();
  const canExport = can('export_data');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // Parse the API-proxy response (body may be a JSON string or already-parsed object).
  const parseBody = (data: any) =>
    data?.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    setDone(null);
    try {
      const token = (await getIdTokenClaims())?.__raw;
      const auth = { Authorization: `Bearer ${token}` };

      // 1) Start the async job.
      const startResp = await fetch(`${API_ENDPOINT}/meters/export`, { headers: auth });
      if (!startResp.ok) throw new Error(`Export failed to start (${startResp.status})`);
      const jobId = parseBody(await startResp.json())?.job_id;
      if (!jobId) throw new Error('The export did not start correctly.');

      // 2) Poll until the workbook is ready (or it errors / times out).
      const deadline = Date.now() + 5 * 60 * 1000; // 5 minutes
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        const stResp = await fetch(
          `${API_ENDPOINT}/meters/export/status?job_id=${encodeURIComponent(jobId)}`,
          { headers: auth },
        );
        if (!stResp.ok) continue; // transient — keep polling
        const st = parseBody(await stResp.json());
        if (st?.status === 'ready' && st.url) {
          // The presigned URL carries a Content-Disposition: attachment header.
          const a = document.createElement('a');
          a.href = st.url;
          a.download = st.filename || 'MFCC_Export.xlsx';
          document.body.appendChild(a);
          a.click();
          a.remove();
          setDone(`Exported ${st.meters} meter/product combos · ${st.provings} provings.`);
          return;
        }
        if (st?.status === 'error') {
          throw new Error(st.error || 'The export failed while generating.');
        }
        // status 'pending' → keep polling
      }
      throw new Error('The export took too long. Please try again.');
    } catch (e: any) {
      setError(e?.message || 'Could not generate the export. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Meter Factor Control Charts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Site meter health and factor control charts.
          </Typography>
        </Box>
        {canExport && (
          <Tooltip title={exporting
            ? 'Generating export…'
            : 'Download all data — an Excel workbook with a tab and control chart per meter/product, plus a Data Extraction tab with every proving.'}>
            <span>
              <IconButton onClick={handleExport} disabled={exporting} color="primary" size="large">
                {exporting ? <CircularProgress size={22} color="inherit" /> : <FileDownload />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Snackbar
        open={Boolean(done)}
        autoHideDuration={6000}
        onClose={() => setDone(null)}
        message={done || ''}
      />

      <MeterGrid />
    </Box>
  );
};

export default MeterFactorControlCharts;
